import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp, Clock, Zap, ArrowUpCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../components/ThemeProvider';
import { usePermissions } from '../components/PermissionsProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UsagePage() {
  const { theme } = useTheme();
  const { userRole } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrialExpiredDialog, setShowTrialExpiredDialog] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/usage/stats`, {
        withCredentials: true
      });
      setStats(response.data);
      
      // Check if trial has expired (for non-superadmin users)
      const subscription = response.data.subscription;
      const trialDaysRemaining = response.data.trial_days_remaining;
      
      // Show expiration modal only for non-superadmin users with expired trial
      if (
        userRole !== 'super_admin' &&
        subscription?.status === 'trial' && 
        subscription?.status !== 'super_admin' &&
        trialDaysRemaining === 0
      ) {
        setShowTrialExpiredDialog(true);
      }
    } catch (error) {
      console.error('[Usage] Error fetching stats:', error);
      toast.error('Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [location]); // Re-fetch when location changes (navigation)

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Activity className="h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))] mx-auto" />
          <p className="text-[hsl(var(--muted-foreground))]">Loading usage statistics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <p className="text-[hsl(var(--muted-foreground))]">No usage data available</p>
      </div>
    );
  }

  const subscription = stats.subscription;
  const isOverLimit = stats.today_usage >= stats.daily_limit;
  const percentageUsed = stats.percentage_used;

  // Get tier color
  const getTierColor = (tier) => {
    switch (tier) {
      case 'starter': return 'bg-[hsl(var(--info))] text-white';
      case 'professional': return 'bg-[hsl(var(--accent))] text-white';
      case 'enterprise': return 'bg-[hsl(var(--success))] text-white';
      default: return 'bg-[hsl(var(--muted))] text-white';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'trial': return 'bg-[hsl(var(--warning))] text-white';
      case 'active': return 'bg-[hsl(var(--success))] text-white';
      case 'super_admin': return 'bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] text-white';
      case 'expired': return 'bg-[hsl(var(--destructive))] text-white';
      case 'cancelled': return 'bg-[hsl(var(--muted))] text-white';
      default: return 'bg-[hsl(var(--muted))] text-white';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'super_admin': return 'Super Admin';
      case 'trial': return 'Trial';
      case 'active': return 'Active';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="usage-page-container">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
          Usage & Billing
        </h1>
        <p className="text-[hsl(var(--muted-foreground))]">
          Monitor your API usage and manage your subscription
        </p>
      </div>

      {/* Subscription Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card data-testid="current-tier-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-semibold capitalize">{subscription.tier}</div>
                <Badge className={`mt-2 ${getTierColor(subscription.tier)}`}>
                  {subscription.tier.toUpperCase()}
                </Badge>
              </div>
              <Zap className="h-8 w-8 text-[hsl(var(--accent))]" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-semibold">{getStatusText(subscription.status)}</div>
                <Badge className={`mt-2 ${getStatusColor(subscription.status)}`}>
                  {getStatusText(subscription.status).toUpperCase()}
                </Badge>
              </div>
              <Activity className="h-8 w-8 text-[hsl(var(--accent))]" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="daily-limit-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
              Daily API Limit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-semibold">{stats.daily_limit.toLocaleString()}</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">calls per day</p>
              </div>
              <TrendingUp className="h-8 w-8 text-[hsl(var(--accent))]" />
            </div>
          </CardContent>
        </Card>

        {subscription.status === 'trial' && subscription.status !== 'super_admin' && stats.trial_days_remaining !== null && (
          <Card data-testid="trial-remaining-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                Trial Remaining
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-semibold">{stats.trial_days_remaining}</div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">days left</p>
                </div>
                <Clock className="h-8 w-8 text-[hsl(var(--warning))]" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Today's Usage */}
      <Card data-testid="today-usage-card">
        <CardHeader>
          <CardTitle>Today's API Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
                {stats.today_usage.toLocaleString()} <span className="text-lg text-[hsl(var(--muted-foreground))]">/ {stats.daily_limit.toLocaleString()}</span>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                {percentageUsed}% of daily limit used
              </p>
            </div>
            {isOverLimit && (
              <Badge className="bg-[hsl(var(--destructive))] text-white">
                Over Limit
              </Badge>
            )}
          </div>

          <Progress value={Math.min(percentageUsed, 100)} className="h-3" />

          {percentageUsed >= 80 && (
            <div className={`p-4 rounded-lg border ${isOverLimit ? 'bg-[hsl(var(--destructive))]/10 border-[hsl(var(--destructive))]' : 'bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]'}`}>
              <div className="flex items-start gap-3">
                <ArrowUpCircle className={`h-5 w-5 mt-0.5 ${isOverLimit ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--warning))]'}`} />
                <div className="flex-1">
                  <p className="font-medium">
                    {isOverLimit ? 'Daily Limit Exceeded' : 'Approaching Daily Limit'}
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    {isOverLimit 
                      ? `You've exceeded your daily limit by ${stats.today_usage - stats.daily_limit} calls. Overage charges will apply at $0.02 per additional call.`
                      : `You're at ${percentageUsed}% of your daily limit. Consider upgrading if you need more capacity.`
                    }
                  </p>
                  <Button
                    size="sm"
                    className="mt-3 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                    onClick={() => navigate('/', { state: { scrollToPricing: true } })}
                  >
                    Upgrade Plan
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 7-Day Usage Chart */}
      <Card data-testid="usage-chart-card">
        <CardHeader>
          <CardTitle>Last 7 Days Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.last_7_days}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? 'hsl(var(--border))' : '#e5e7eb'} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme === 'dark' ? 'hsl(var(--muted-foreground))' : '#6b7280'}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis stroke={theme === 'dark' ? 'hsl(var(--muted-foreground))' : '#6b7280'} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme === 'dark' ? 'hsl(var(--card))' : 'white',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Bar dataKey="calls" fill="hsl(var(--accent))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Details */}
      <Card data-testid="subscription-details-card">
        <CardHeader>
          <CardTitle>Subscription Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-3">Plan Features</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))]">Daily API Calls</span>
                  <span className="font-medium">{stats.daily_limit.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))]">Current Tier</span>
                  <span className="font-medium capitalize">{subscription.tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))]">Status</span>
                  <Badge className={getStatusColor(subscription.status)}>
                    {subscription.status.toUpperCase()}
                  </Badge>
                </div>
                {subscription.status === 'trial' && subscription.status !== 'super_admin' && stats.trial_days_remaining !== null && (
                  <div className="flex justify-between">
                    <span className="text-[hsl(var(--muted-foreground))]">Trial Ends In</span>
                    <span className="font-medium">{stats.trial_days_remaining} days</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3">Usage Limits</h3>
              <div className="space-y-2 text-sm">
                {subscription.tier === 'starter' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">RFQs per Month</span>
                      <span className="font-medium">3</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">Insurance Policies</span>
                      <span className="font-medium">Quote only</span>
                    </div>
                  </>
                )}
                {subscription.tier === 'professional' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">RFQs per Month</span>
                      <span className="font-medium">25</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">Insurance Policies</span>
                      <span className="font-medium">10/month</span>
                    </div>
                  </>
                )}
                {subscription.tier === 'enterprise' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">RFQs per Month</span>
                      <span className="font-medium">Unlimited</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[hsl(var(--muted-foreground))]">Insurance Policies</span>
                      <span className="font-medium">Unlimited</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))]">Overage Rate</span>
                  <span className="font-medium">
                    {subscription.tier === 'enterprise' ? '$0.01' : '$0.02'} per call
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade CTA */}
          {(subscription.status === 'trial' || subscription.tier === 'starter') && (
            <div className="mt-6 p-4 rounded-lg bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Unlock More Capacity</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    Upgrade to Professional or Enterprise for higher limits and advanced features
                  </p>
                </div>
                <Button
                  className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={() => navigate('/', { state: { scrollToPricing: true } })}
                  data-testid="upgrade-plan-button"
                >
                  View Plans
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trial Expired Dialog */}
      <Dialog open={showTrialExpiredDialog} onOpenChange={setShowTrialExpiredDialog}>
        <DialogContent data-testid="trial-expired-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[hsl(var(--destructive))]" />
              Your Trial Has Ended
            </DialogTitle>
            <DialogDescription>
              Your 14-day free trial has expired. Choose a plan to continue accessing Maritime Pulse's premium features.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Select a subscription plan to unlock:
            </p>
            <ul className="text-sm space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--primary))]">•</span>
                Unlimited vessel tracking and real-time updates
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--primary))]">•</span>
                Advanced trade analytics and arbitrage detection
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--primary))]">•</span>
                Freight insurance quotes and risk assessment
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--primary))]">•</span>
                AI-powered marketplace with vessel-cargo matching
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTrialExpiredDialog(false)}
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                setShowTrialExpiredDialog(false);
                navigate('/#pricing');
                window.scrollTo({ top: document.getElementById('pricing')?.offsetTop || 0, behavior: 'smooth' });
              }}
              data-testid="view-pricing-button"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              View Pricing Plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
